/**
 * Phase 5 — custom restaurant form dialog.
 *
 * Restaurants now carry a type discriminator (regular | specialty) and the
 * field surface differs sharply between the two:
 *
 *   Regular  : lunch_menus[] + dinner_menus[] + has_dessert_rotation +
 *              optional lunch_dessert_menus[] / dinner_dessert_menus[].
 *   Specialty: fixed_menu_label + meals_served (chip multi-select) +
 *              default_times {start, end} per selected meal.
 *
 * Validation mirrors the backend's RestaurantIn validator; we surface
 * errors inline so the user doesn't get 422-mystery toasts.
 */
import { useState, useEffect } from "react";
// Phase 16 (F8/CO3) — side-by-side menu comparison dialog.
import CompareMenusDialog from "@/components/CompareMenusDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { createLibraryItem, updateLibraryItem, listLibrary } from "@/apiClient";
import { Loader2, Plus, X } from "lucide-react";

const MEAL_OPTIONS = [
  "embarkation_breakfast", "breakfast", "brunch", "lunch",
  "embarkation_lunch", "tea_time", "embarkation_dinner", "dinner",
];
const MEAL_LABELS = {
  embarkation_breakfast: "Embarkation Breakfast",
  breakfast: "Breakfast",
  brunch: "Brunch",
  lunch: "Lunch",
  embarkation_lunch: "Embarkation Lunch",
  tea_time: "Tea Time",
  embarkation_dinner: "Embarkation Dinner",
  dinner: "Dinner",
};
const DEFAULT_TIME_BY_MEAL = {
  embarkation_breakfast: { start: "06:00", end: "08:00" },
  breakfast: { start: "07:00", end: "10:00" },
  brunch: { start: "10:30", end: "13:00" },
  lunch: { start: "12:00", end: "14:30" },
  embarkation_lunch: { start: "11:00", end: "15:00" },
  tea_time: { start: "15:30", end: "17:00" },
  embarkation_dinner: { start: "17:00", end: "20:00" },
  dinner: { start: "18:00", end: "22:00" },
};

// Phase 7+ / Phase 13.1 — regular restaurants can optionally declare default
// times for any of these eight service slots. Order matters: the form
// renders rows top-to-bottom in time-of-day order so it reads naturally.
const REGULAR_SLOTS = [
  { key: "embarkation_breakfast", label: "Embarkation Breakfast", hint: "Day 1 only · early seating" },
  { key: "breakfast",             label: "Breakfast",             hint: "Sit-down breakfast (e.g. MDR)" },
  { key: "brunch",                label: "Brunch",                hint: "Late-morning brunch (sea-day favourite)" },
  { key: "lunch",                 label: "Lunch",                 hint: "Default lunch window" },
  { key: "embarkation_lunch",     label: "Embarkation Lunch",     hint: "Day 1 only · extended" },
  { key: "tea_time",              label: "Tea Time",              hint: "Afternoon tea / snack service" },
  { key: "embarkation_dinner",    label: "Embarkation Dinner",    hint: "Day 1 only · sail-away seating" },
  { key: "dinner",                label: "Dinner",                hint: "Default dinner window" },
];

// Phase 7+ / Phase 13.1 — buffets pick from this set in their
// meals_offered chip selector. All 3 embarkation slots are now opt-in
// per-buffet (Day 1 only, surfaced in the meal plan editor).
const BUFFET_MEALS = [
  { key: "embarkation_breakfast", label: "Embarkation Breakfast" },
  { key: "breakfast",             label: "Breakfast" },
  { key: "brunch",                label: "Brunch" },
  { key: "lunch",                 label: "Lunch" },
  { key: "embarkation_lunch",     label: "Embarkation Lunch" },
  { key: "tea_time",              label: "Tea Time" },
  { key: "embarkation_dinner",    label: "Embarkation Dinner" },
  { key: "dinner",                label: "Dinner" },
];

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const isHHMM = (s) => HHMM_RE.test(String(s || ""));
const isEndAfterStart = (start, end) => isHHMM(start) && isHHMM(end) && end > start;

const blankRegular = () => ({
  name: "",
  type: "regular",
  enabled: true,
  sort_order: 100,
  // Phase 7+ — optional breakfast / brunch / tea_time service. Empty list
  // = restaurant doesn't serve that meal; UI hides the cell.
  breakfast_menus: [],
  brunch_menus: [],
  tea_time_menus: [],
  lunch_menus: [""],
  dinner_menus: [""],
  has_dessert_rotation: false,
  lunch_dessert_menus: [],
  dinner_dessert_menus: [],
  fixed_menu_label: "",
  meals_served: [],
  meals_offered: [],
  default_times: {},
  // Phase 7++ — opt-in festivals at the venue level. When ON, the meal
  // plan editor surfaces a per-day Festivals editor for this venue.
  hosts_festivals: false,
  // Phase 13.4 — Assignment. Three optional fields snapshot id+name+role
  // for both chef refs so labels survive staff-doc deletion.
  overall_chef_id: null,
  overall_chef_name: "",
  overall_chef_role: "",
  outlet_chef_id: null,
  outlet_chef_name: "",
  outlet_chef_role: "",
  deck_phone: "",
});

const blankSpecialty = () => ({
  name: "",
  type: "specialty",
  enabled: true,
  sort_order: 100,
  breakfast_menus: [],
  brunch_menus: [],
  tea_time_menus: [],
  lunch_menus: [],
  dinner_menus: [],
  has_dessert_rotation: false,
  lunch_dessert_menus: [],
  dinner_dessert_menus: [],
  fixed_menu_label: "",
  meals_served: ["dinner"],
  meals_offered: [],
  default_times: { dinner: { ...DEFAULT_TIME_BY_MEAL.dinner } },
  hosts_festivals: false,
  // Phase 13.4 — Assignment (mirrors blankRegular).
  overall_chef_id: null,
  overall_chef_name: "",
  overall_chef_role: "",
  outlet_chef_id: null,
  outlet_chef_name: "",
  outlet_chef_role: "",
  deck_phone: "",
});

// Phase 7+ — buffet type. Just name + sort_order + enabled + meals_offered.
// Buffet themes + per-meal content live on meal_plan.buffets[], not in
// the library.
const blankBuffet = () => ({
  name: "",
  type: "buffet",
  enabled: true,
  sort_order: 100,
  breakfast_menus: [],
  brunch_menus: [],
  tea_time_menus: [],
  lunch_menus: [],
  dinner_menus: [],
  has_dessert_rotation: false,
  lunch_dessert_menus: [],
  dinner_dessert_menus: [],
  fixed_menu_label: "",
  meals_served: [],
  // Phase 7+ — start with the classic three. User can opt in to Brunch
  // + Tea Time on a per-buffet basis.
  meals_offered: ["breakfast", "lunch", "dinner"],
  default_times: {},
  // Phase 13.4 — Assignment (mirrors blankRegular).
  overall_chef_id: null,
  overall_chef_name: "",
  overall_chef_role: "",
  outlet_chef_id: null,
  outlet_chef_name: "",
  outlet_chef_role: "",
  deck_phone: "",
});

function hydrate(item) {
  // Normalise an existing item so all expected keys exist (avoids
  // controlled/uncontrolled input warnings on edit).
  const base = item.type === "specialty" ? blankSpecialty() : blankRegular();
  const merged = { ...base, ...item };
  // Make sure the menus aren't empty arrays for regular (need at least one
  // row to display in the editor).
  if (merged.type === "regular") {
    if (!Array.isArray(merged.lunch_menus) || merged.lunch_menus.length === 0)
      merged.lunch_menus = [""];
    if (!Array.isArray(merged.dinner_menus) || merged.dinner_menus.length === 0)
      merged.dinner_menus = [""];
  }
  return merged;
}

export default function RestaurantFormDialog({
  open,
  onOpenChange,
  editingItem,
  onSuccess,
}) {
  const isEdit = !!editingItem;
  const [formData, setFormData] = useState(blankRegular());
  // Phase 7 — Service Times collapsible section for regular restaurants.
  // Auto-expands on edit when the restaurant already has any default_times
  // set, otherwise stays collapsed by default to keep the form short.
  const [showTimes, setShowTimes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Phase 16 (F8/CO3) — Compare Menus dialog state. `null` = closed.
  const [compareTarget, setCompareTarget] = useState(null);
  // Phase 13.4 — staff library for the Assignment chef pickers. Loaded
  // once when the dialog opens; soft-fails to an empty list if the
  // network or library is unreachable so the rest of the form stays
  // usable.
  const [staffLib, setStaffLib] = useState([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    listLibrary("staff")
      .then((rows) => { if (!cancelled) setStaffLib(rows || []); })
      .catch(() => { if (!cancelled) setStaffLib([]); });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editingItem) {
      const clone = { ...editingItem };
      delete clone.id;
      delete clone.created_at;
      delete clone.updated_at;
      const hydrated = hydrate(clone);
      setFormData(hydrated);
      // Phase 7 — auto-expand Service Times when the edited regular doc
      // already has any default_times saved so the user can see them.
      setShowTimes(
        hydrated.type === "regular" &&
        Object.keys(hydrated.default_times || {}).length > 0,
      );
    } else {
      setFormData(blankRegular());
      setShowTimes(false);
    }
    setError(null);
  }, [open, editingItem]);

  const setType = (type) => {
    setFormData(() => {
      const carry = { name: formData.name, sort_order: formData.sort_order };
      if (type === "specialty")  return { ...blankSpecialty(), ...carry };
      if (type === "buffet")     return { ...blankBuffet(),    ...carry };
      return { ...blankRegular(),   ...carry };
    });
  };

  const setField = (key, value) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  // Phase 16 (F8/CO3) — menu rotations now hold MenuEntry objects
  // ({id, name, rating, comment, courses[]}) instead of bare strings.
  // Helpers below normalise the form state so the list editor can
  // safely render legacy str / mixed entries.
  const _newMenuEntry = (name = "") => ({
    id: (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    name,
    rating: null,
    comment: null,
    courses: [],
  });
  const _coerceMenuEntry = (m) => {
    if (m == null) return _newMenuEntry("");
    if (typeof m === "string") return _newMenuEntry(m);
    if (typeof m === "object") {
      return {
        id: m.id || _newMenuEntry().id,
        name: m.name ?? "",
        rating: m.rating ?? null,
        comment: m.comment ?? null,
        courses: Array.isArray(m.courses)
          ? m.courses.map((c) => ({
              id: c?.id || `c_${Math.random().toString(36).slice(2, 8)}`,
              name: c?.name ?? "",
              dishes: Array.isArray(c?.dishes) ? c.dishes.filter(Boolean) : [],
            }))
          : [],
      };
    }
    return _newMenuEntry("");
  };

  const updateMenuList = (key, idx, patch) =>
    setFormData((prev) => {
      const next = (prev[key] || []).map(_coerceMenuEntry);
      // `patch` may be a plain string (legacy MenuList input) — treat
      // that as renaming `.name`.
      next[idx] = typeof patch === "string"
        ? { ...next[idx], name: patch }
        : { ...next[idx], ...patch };
      return { ...prev, [key]: next };
    });
  const addMenuItem = (key) =>
    setFormData((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []).map(_coerceMenuEntry), _newMenuEntry("")],
    }));
  const removeMenuItem = (key, idx) =>
    setFormData((prev) => {
      const next = (prev[key] || []).map(_coerceMenuEntry);
      next.splice(idx, 1);
      return { ...prev, [key]: next };
    });

  const toggleMeal = (meal) => {
    setFormData((prev) => {
      const selected = prev.meals_served.includes(meal);
      let next_meals;
      let next_times;
      if (selected) {
        next_meals = prev.meals_served.filter((m) => m !== meal);
        next_times = { ...prev.default_times };
        delete next_times[meal];
      } else {
        next_meals = [...prev.meals_served, meal];
        next_times = {
          ...prev.default_times,
          [meal]: { ...DEFAULT_TIME_BY_MEAL[meal] },
        };
      }
      return { ...prev, meals_served: next_meals, default_times: next_times };
    });
  };

  const setMealTime = (meal, side, value) =>
    setFormData((prev) => ({
      ...prev,
      default_times: {
        ...prev.default_times,
        [meal]: {
          ...(prev.default_times[meal] || DEFAULT_TIME_BY_MEAL[meal] || { start: "", end: "" }),
          [side]: value,
        },
      },
    }));

  // Phase 7 — regular restaurants share the same default_times map but
  // their slot keys (embarkation_breakfast / breakfast / lunch /
  // embarkation_lunch / dinner) are independent of meals_served. The
  // "Clear" button wipes the slot entirely so the payload omits it.
  const setRegularSlotTime = (slot, side, value) =>
    setFormData((prev) => ({
      ...prev,
      default_times: {
        ...prev.default_times,
        [slot]: {
          ...(prev.default_times[slot] || { start: "", end: "" }),
          [side]: value,
        },
      },
    }));

  const clearRegularSlot = (slot) =>
    setFormData((prev) => {
      const next = { ...(prev.default_times || {}) };
      delete next[slot];
      return { ...prev, default_times: next };
    });

  const fillRegularSlotDefault = (slot) =>
    setFormData((prev) => ({
      ...prev,
      default_times: {
        ...prev.default_times,
        [slot]: { ...DEFAULT_TIME_BY_MEAL[slot] },
      },
    }));

  // Phase 16 (F8/CO3) — emit MenuEntry-shaped objects (drop empties by
  // `.name`). Backend `_migrate_menu_list` accepts strings too but we
  // standardise to the new shape so post-save reads round-trip cleanly.
  const cleanLists = (lists) =>
    (lists || [])
      .map(_coerceMenuEntry)
      .filter((m) => (m.name || "").trim().length > 0)
      .map((m) => ({
        id: m.id,
        name: (m.name || "").trim(),
        rating: m.rating || null,
        comment: (m.comment || "").trim() || null,
        courses: (m.courses || [])
          .map((c) => ({
            id: c.id,
            name: (c.name || "").trim(),
            dishes: (c.dishes || []).map((d) => (d || "").trim()).filter(Boolean),
          }))
          .filter((c) => c.name || c.dishes.length > 0),
      }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Build payload that mirrors the backend RestaurantIn validator's expectations.
    const payload = {
      name: (formData.name || "").trim(),
      type: formData.type,
      enabled: !!formData.enabled,
      sort_order: Number(formData.sort_order) || 0,
      // Phase 7++ — venue-level festivals opt-in. Regular + specialty
      // surface this in the form; buffets ignore it (always per-meal).
      hosts_festivals: !!formData.hosts_festivals,
    };

    if (formData.type === "regular") {
      payload.breakfast_menus = cleanLists(formData.breakfast_menus || []);
      payload.brunch_menus = cleanLists(formData.brunch_menus || []);
      payload.tea_time_menus = cleanLists(formData.tea_time_menus || []);
      payload.lunch_menus = cleanLists(formData.lunch_menus || []);
      payload.dinner_menus = cleanLists(formData.dinner_menus || []);
      payload.has_dessert_rotation = !!formData.has_dessert_rotation;
      if (payload.has_dessert_rotation) {
        payload.lunch_dessert_menus = cleanLists(formData.lunch_dessert_menus || []);
        payload.dinner_dessert_menus = cleanLists(formData.dinner_dessert_menus || []);
      } else {
        payload.lunch_dessert_menus = [];
        payload.dinner_dessert_menus = [];
      }
      // Phase 7 — emit only slots where BOTH start and end are valid HH:MM.
      // Empty / partial / invalid slots are silently dropped so the backend
      // validator (TimeRange pattern) doesn't 422 on a half-filled row.
      const cleanedTimes = {};
      for (const { key } of REGULAR_SLOTS) {
        const t = (formData.default_times || {})[key];
        if (t && isHHMM(t.start) && isHHMM(t.end)) {
          cleanedTimes[key] = { start: t.start, end: t.end };
        }
      }
      payload.default_times = cleanedTimes;
      if (!payload.name) return setError("Name is required.");
      // Phase 7+ — permissive: any non-empty menu rotation OR any
      // default_times entry counts as a valid service signal. Le Petit
      // Chef with only dinner is fine. Tea-house with only tea_time is
      // fine. Placeholder with only default_times is fine.
      const hasAnyMenu =
        payload.breakfast_menus.length > 0 ||
        payload.brunch_menus.length > 0 ||
        payload.lunch_menus.length > 0 ||
        payload.tea_time_menus.length > 0 ||
        payload.dinner_menus.length > 0;
      const hasAnyDefaultTime = Object.keys(payload.default_times).length > 0;
      if (!hasAnyMenu && !hasAnyDefaultTime) {
        // Soft warning — let the user proceed deliberately.
        const ok = window.confirm(
          "This restaurant has no meal services configured yet (no menus, no default times). Save anyway?"
        );
        if (!ok) return;
      }
    } else if (formData.type === "buffet") {
      // Phase 7+ — buffet payload also carries `meals_offered` (which
      // meals this buffet serves). Defaults seeded by blankBuffet().
      payload.meals_offered = (formData.meals_offered || []).filter(
        (m) => ["breakfast", "brunch", "lunch", "tea_time", "dinner"].includes(m),
      );
      if (payload.meals_offered.length === 0) {
        // Tolerated by the backend (it falls back to the classic three),
        // but warn the user — empty selection is almost always a mistake.
        return setError("Pick at least one meal in 'Meals Offered'.");
      }
      if (!payload.name) return setError("Name is required.");
    } else {
      payload.fixed_menu_label = (formData.fixed_menu_label || "").trim();
      payload.meals_served = formData.meals_served;
      payload.default_times = formData.default_times;
      if (!payload.name) return setError("Name is required.");
      if (payload.meals_served.length === 0)
        return setError("Specialty restaurants need at least one meal in meals_served.");
    }

    setSaving(true);
    try {
      if (isEdit) {
        await updateLibraryItem("restaurants", editingItem.id, payload);
      } else {
        await createLibraryItem("restaurants", payload);
      }
      onSuccess();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Save failed";
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Phase 16 (F8/CO3) — Compare Menus dialog. Open is gated on
          compareTarget so we can pass the slot label + the menus array. */}
      {compareTarget && (
        <CompareMenusDialog
          open
          onOpenChange={(o) => { if (!o) setCompareTarget(null); }}
          menus={compareTarget.menus}
          slotLabel={compareTarget.slot}
        />
      )}
      <DialogContent
        lockOutsideClick
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        data-testid="restaurant-form-dialog"
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEdit ? "Edit" : "Create"} Restaurant
            </DialogTitle>
            <DialogDescription>
              Restaurants are split into <b>Regular</b> (dining rooms with menu
              rotations) and <b>Specialty</b> (per-meal services).
            </DialogDescription>
          </DialogHeader>

          {/* ──────────────────────────── Name + Type radio */}
          <div className="my-4 space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr]">
              <div className="space-y-1.5">
                <Label htmlFor="rf-name">Name</Label>
                <Input
                  id="rf-name"
                  value={formData.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="e.g. MDR, Tuscan Grille"
                  required
                  data-testid="form-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rf-sort">Sort Order</Label>
                <Input
                  id="rf-sort"
                  type="number"
                  min={0}
                  max={9999}
                  value={formData.sort_order}
                  onChange={(e) =>
                    setField("sort_order", parseInt(e.target.value, 10) || 0)
                  }
                  data-testid="form-sort-order-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Type</Label>
              <div className="grid grid-cols-3 gap-2" role="radiogroup">
                {["regular", "specialty", "buffet"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={formData.type === t}
                    onClick={() => setType(t)}
                    data-testid={`form-type-${t}`}
                    className={cn(
                      "rounded-md border px-3 py-3 text-left transition-colors",
                      formData.type === t
                        ? t === "buffet"
                          ? "border-teal-500 bg-teal-50 text-teal-900"
                          : "border-primary bg-primary/5 text-primary"
                        : "border-input bg-background hover:bg-accent"
                    )}
                  >
                    <div className="text-sm font-semibold capitalize">{t}</div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground leading-tight">
                      {t === "regular"
                        ? "Dining rooms with menu rotations + open/closed per side"
                        : t === "specialty"
                          ? "Per-meal service with toggleable open + custom times"
                          : "Themed buffet venues (e.g. OVC, Windjammer). Just a name."}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <Label htmlFor="rf-enabled" className="cursor-pointer">
                Enabled
              </Label>
              <Switch
                id="rf-enabled"
                checked={!!formData.enabled}
                onCheckedChange={(v) => setField("enabled", v)}
                data-testid="form-enabled"
              />
            </div>

            {/* Phase 7++ — Festivals opt-in (regular + specialty only).
                When ON, the meal-plan editor surfaces a per-day venue-
                level Festivals editor for this restaurant. Buffets always
                have festivals per meal — toggle is hidden there. */}
            {(formData.type === "regular" || formData.type === "specialty") && (
              <div className="flex items-center justify-between rounded-md border border-rose-200 bg-rose-50/40 px-3 py-2">
                <div className="flex flex-col">
                  <Label htmlFor="rf-hosts-festivals" className="cursor-pointer">
                    Hosts Festivals
                  </Label>
                  <span className="text-[10px] text-muted-foreground">
                    Surface a per-day Festivals editor for this venue.
                  </span>
                </div>
                <Switch
                  id="rf-hosts-festivals"
                  checked={!!formData.hosts_festivals}
                  onCheckedChange={(v) => setField("hosts_festivals", v)}
                  data-testid="form-hosts-festivals"
                />
              </div>
            )}

            {/* ─────────────────────────── REGULAR fields */}
            {formData.type === "regular" && (
              <div className="space-y-4" data-testid="form-regular-fields">
                {/* Phase 7+ — optional breakfast / brunch / tea_time menus.
                    Empty list = restaurant doesn't serve that meal. */}
                <div className="grid grid-cols-1 gap-3 rounded-md border border-sky-200 bg-sky-50/40 p-3 md:grid-cols-3">
                  <MenuList
                    label="Breakfast Menus (optional)"
                    items={formData.breakfast_menus}
                    onUpdate={(idx, v) => updateMenuList("breakfast_menus", idx, v)}
                    onAdd={() => addMenuItem("breakfast_menus")}
                    onRemove={(idx) => removeMenuItem("breakfast_menus", idx)}
                    testIdBase="form-breakfast-menus"
                    helpText="Empty = no breakfast service."
                  />
                  <MenuList
                    label="Brunch Menus (optional)"
                    items={formData.brunch_menus}
                    onUpdate={(idx, v) => updateMenuList("brunch_menus", idx, v)}
                    onAdd={() => addMenuItem("brunch_menus")}
                    onRemove={(idx) => removeMenuItem("brunch_menus", idx)}
                    testIdBase="form-brunch-menus"
                    helpText="Empty = no brunch service."
                  />
                  <MenuList
                    label="Tea Time Menus (optional)"
                    items={formData.tea_time_menus}
                    onUpdate={(idx, v) => updateMenuList("tea_time_menus", idx, v)}
                    onAdd={() => addMenuItem("tea_time_menus")}
                    onRemove={(idx) => removeMenuItem("tea_time_menus", idx)}
                    testIdBase="form-tea-time-menus"
                    helpText="Empty = no tea time service."
                  />
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <MenuList
                    label="Lunch Menus"
                    items={formData.lunch_menus}
                    onUpdate={(idx, v) => updateMenuList("lunch_menus", idx, v)}
                    onAdd={() => addMenuItem("lunch_menus")}
                    onRemove={(idx) => removeMenuItem("lunch_menus", idx)}
                    testIdBase="form-lunch-menus"
                    onCompare={() => setCompareTarget({ slot: "Lunch Menus", menus: formData.lunch_menus || [] })}
                  />
                  <MenuList
                    label="Dinner Menus"
                    items={formData.dinner_menus}
                    onUpdate={(idx, v) => updateMenuList("dinner_menus", idx, v)}
                    onAdd={() => addMenuItem("dinner_menus")}
                    onRemove={(idx) => removeMenuItem("dinner_menus", idx)}
                    testIdBase="form-dinner-menus"
                    onCompare={() => setCompareTarget({ slot: "Dinner Menus", menus: formData.dinner_menus || [] })}
                  />
                </div>

                <div className="flex items-center justify-between rounded-md border px-3 py-2">
                  <Label htmlFor="rf-dessert" className="cursor-pointer">
                    Dessert Rotation
                  </Label>
                  <Switch
                    id="rf-dessert"
                    checked={!!formData.has_dessert_rotation}
                    onCheckedChange={(v) => setField("has_dessert_rotation", v)}
                    data-testid="form-has-dessert-rotation"
                  />
                </div>

                {formData.has_dessert_rotation && (
                  <div className="grid grid-cols-1 gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 md:grid-cols-2">
                    <MenuList
                      label="Lunch Dessert Menus"
                      items={formData.lunch_dessert_menus}
                      onUpdate={(idx, v) =>
                        updateMenuList("lunch_dessert_menus", idx, v)
                      }
                      onAdd={() => addMenuItem("lunch_dessert_menus")}
                      onRemove={(idx) => removeMenuItem("lunch_dessert_menus", idx)}
                      testIdBase="form-lunch-dessert-menus"
                    />
                    <MenuList
                      label="Dinner Dessert Menus"
                      items={formData.dinner_dessert_menus}
                      onUpdate={(idx, v) =>
                        updateMenuList("dinner_dessert_menus", idx, v)
                      }
                      onAdd={() => addMenuItem("dinner_dessert_menus")}
                      onRemove={(idx) =>
                        removeMenuItem("dinner_dessert_menus", idx)
                      }
                      testIdBase="form-dinner-dessert-menus"
                    />
                  </div>
                )}

                {/* ─────────────── Phase 7 — Service Times (optional) */}
                <RegularServiceTimes
                  defaultTimes={formData.default_times || {}}
                  expanded={showTimes}
                  onToggleExpanded={() => setShowTimes((v) => !v)}
                  onSet={setRegularSlotTime}
                  onClear={clearRegularSlot}
                  onFillDefault={fillRegularSlotDefault}
                />
              </div>
            )}

            {/* ─────────────────────────── BUFFET fields (Phase 7+) */}
            {formData.type === "buffet" && (
              <div className="space-y-4" data-testid="form-buffet-fields">
                <div className="rounded-md border bg-amber-50/40 p-3">
                  <Label className="text-xs font-semibold uppercase tracking-wider">
                    Meals Offered
                  </Label>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    Which meals does this buffet serve? Embarkation Lunch
                    is added automatically on Day 1, no need to tick it.
                  </p>
                  <div
                    className="mt-2 flex flex-wrap gap-1.5"
                    data-testid="form-buffet-meals-offered"
                  >
                    {BUFFET_MEALS.map(({ key, label }) => {
                      const checked = (formData.meals_offered || []).includes(key);
                      return (
                        <button
                          type="button"
                          key={key}
                          onClick={() =>
                            setFormData((prev) => {
                              const set = new Set(prev.meals_offered || []);
                              if (set.has(key)) set.delete(key);
                              else set.add(key);
                              // preserve canonical order
                              const ordered = BUFFET_MEALS
                                .map((bm) => bm.key)
                                .filter((k) => set.has(k));
                              return { ...prev, meals_offered: ordered };
                            })
                          }
                          className={
                            "rounded-full border px-2.5 py-1 text-xs font-medium transition " +
                            (checked
                              ? "border-amber-500 bg-amber-500 text-white shadow-sm"
                              : "border-amber-300 bg-white text-amber-900 hover:bg-amber-100")
                          }
                          data-testid={`form-buffet-meal-${key}`}
                          aria-pressed={checked}
                        >
                          {checked ? "✓ " : "+ "}
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  {(formData.meals_offered || []).length === 0 && (
                    <p className="mt-1 text-[10px] text-destructive">
                      Pick at least one meal.
                    </p>
                  )}
                </div>
                <p className="text-[10px] italic text-muted-foreground">
                  Buffets only carry a name + which meals they offer. Per-day
                  themes, hot/cold/pastry, lines, live stations and festivals
                  are configured per voyage day in the Meal Plan editor.
                </p>
              </div>
            )}

            {/* ─────────────────────────── SPECIALTY fields */}
            {formData.type === "specialty" && (
              <div className="space-y-4" data-testid="form-specialty-fields">
                <div className="space-y-1.5">
                  <Label htmlFor="rf-fml">Fixed Menu Label</Label>
                  <Input
                    id="rf-fml"
                    value={formData.fixed_menu_label}
                    onChange={(e) => setField("fixed_menu_label", e.target.value)}
                    placeholder="e.g. Tuscan Set Menu"
                    data-testid="form-fixed-menu-label"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Optional, display-only.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label>Meals Served</Label>
                  <div className="flex flex-wrap gap-2" data-testid="form-meals-served">
                    {MEAL_OPTIONS.map((m) => {
                      const sel = formData.meals_served.includes(m);
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={() => toggleMeal(m)}
                          data-testid={`form-meals-served-${m}`}
                          className={cn(
                            "rounded-full border px-3 py-1 text-xs font-medium capitalize transition-colors",
                            sel
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-input bg-background hover:bg-accent"
                          )}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {formData.meals_served.length > 0 && (
                  <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                    <Label className="text-xs">Default Times</Label>
                    {formData.meals_served.map((meal) => {
                      const t = formData.default_times[meal] || { start: "", end: "" };
                      return (
                        <div
                          key={meal}
                          className="grid grid-cols-[80px_1fr_1fr] items-center gap-2"
                          data-testid={`form-time-row-${meal}`}
                        >
                          <span className="text-xs font-semibold uppercase">
                            {meal}
                          </span>
                          <Input
                            type="time"
                            value={t.start || ""}
                            onChange={(e) =>
                              setMealTime(meal, "start", e.target.value)
                            }
                            className="h-8 text-xs"
                            data-testid={`form-time-${meal}-start`}
                          />
                          <Input
                            type="time"
                            value={t.end || ""}
                            onChange={(e) =>
                              setMealTime(meal, "end", e.target.value)
                            }
                            className="h-8 text-xs"
                            data-testid={`form-time-${meal}-end`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {error && (
            <div
              className="mb-4 rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive"
              data-testid="form-error"
            >
              {error}
            </div>
          )}

          {/* Phase 13.4 — Assignment section. Two optional chef refs +
              free-text deck phone. Applies to all 3 venue types; the
              same compact 3-row layout regardless of `formData.type`. */}
          <div
            className="mb-4 rounded-md border border-slate-200 bg-slate-50/50 p-3"
            data-testid="rf-assignment-section"
          >
            <div className="mb-2 flex items-center gap-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Assignment
              </Label>
              <span className="text-[10px] italic text-muted-foreground">
                (optional — surfaces on day editor + PDFs)
              </span>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <Label className="text-[11px] font-medium text-slate-600">
                  Overall Chef in Charge
                </Label>
                <Select
                  value={formData.overall_chef_id || "__empty__"}
                  onValueChange={(v) => {
                    if (v === "__empty__") {
                      setField("overall_chef_id", null);
                      setField("overall_chef_name", "");
                      setField("overall_chef_role", "");
                      return;
                    }
                    const lib = staffLib.find((s) => s.id === v);
                    setField("overall_chef_id", v);
                    setField("overall_chef_name", lib?.name || "");
                    setField("overall_chef_role", lib?.role || "");
                  }}
                >
                  <SelectTrigger
                    className="h-8 text-xs"
                    data-testid="rf-overall-chef-select"
                  >
                    <SelectValue placeholder="Pick a staff member…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__empty__">(None)</SelectItem>
                    {staffLib.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}{s.role ? ` — ${s.role}` : ""}{s.deck_phone ? ` · ext ${s.deck_phone}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] font-medium text-slate-600">
                  Outlet in Charge
                </Label>
                <Select
                  value={formData.outlet_chef_id || "__empty__"}
                  onValueChange={(v) => {
                    if (v === "__empty__") {
                      setField("outlet_chef_id", null);
                      setField("outlet_chef_name", "");
                      setField("outlet_chef_role", "");
                      return;
                    }
                    const lib = staffLib.find((s) => s.id === v);
                    setField("outlet_chef_id", v);
                    setField("outlet_chef_name", lib?.name || "");
                    setField("outlet_chef_role", lib?.role || "");
                  }}
                >
                  <SelectTrigger
                    className="h-8 text-xs"
                    data-testid="rf-outlet-chef-select"
                  >
                    <SelectValue placeholder="Pick a staff member…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__empty__">(None)</SelectItem>
                    {staffLib.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}{s.role ? ` — ${s.role}` : ""}{s.deck_phone ? ` · ext ${s.deck_phone}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[11px] font-medium text-slate-600">
                  Deck Phone Number
                </Label>
                <Input
                  value={formData.deck_phone || ""}
                  onChange={(e) => setField("deck_phone", e.target.value)}
                  placeholder="e.g. 1234"
                  className="h-8 text-xs"
                  data-testid="rf-deck-phone-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              data-testid="form-cancel-btn"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="form-submit-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Phase 16 (F8/CO3) — rich rotation card editor. Each menu rotation is
// a MenuEntry: name + rating (Strong/Medium/Weak/Unrated) + comment +
// courses[{id,name,dishes[]}]. Cards collapse to just the name row by
// default; clicking the chevron reveals the inner editor.
function MenuList({ label, items, onUpdate, onAdd, onRemove, testIdBase, onCompare }) {
  const ratings = [
    { v: null,      label: "—",      color: "bg-slate-100 text-slate-600" },
    { v: "strong",  label: "Strong", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    { v: "medium",  label: "Medium", color: "bg-amber-100 text-amber-800 border-amber-300" },
    { v: "weak",    label: "Weak",   color: "bg-rose-100 text-rose-800 border-rose-300" },
  ];
  return (
    <div className="space-y-1.5" data-testid={`${testIdBase}-wrap`}>
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5">
          {label}
          <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
            {items.length}
          </Badge>
        </Label>
        {onCompare && items.length >= 2 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCompare}
            className="h-6 gap-1 text-[10px] text-amber-700 hover:bg-amber-50"
            data-testid={`${testIdBase}-compare`}
          >
            Compare Menus
          </Button>
        )}
      </div>
      <div className="space-y-1.5">
        {items.map((item, idx) => (
          <MenuRotationCard
            key={item?.id || idx}
            item={typeof item === "object" && item !== null ? item : { name: item || "", rating: null, comment: null, courses: [] }}
            onUpdate={(patch) => onUpdate(idx, patch)}
            onRemove={() => onRemove(idx)}
            idx={idx}
            ratings={ratings}
            testIdBase={`${testIdBase}-${idx}`}
          />
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onAdd}
          data-testid={`${testIdBase}-add`}
        >
          <Plus className="h-3.5 w-3.5" />
          Add menu
        </Button>
      </div>
    </div>
  );
}

function MenuRotationCard({ item, onUpdate, onRemove, idx, ratings, testIdBase }) {
  const [expanded, setExpanded] = useState(false);
  const rating = ratings.find((r) => r.v === (item.rating || null)) || ratings[0];
  return (
    <div className="rounded border bg-slate-50/40">
      <div className="flex items-center gap-1.5 p-1.5">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-white"
          data-testid={`${testIdBase}-toggle`}
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          <span className="inline-block w-3 text-center text-[10px]">
            {expanded ? "▾" : "▸"}
          </span>
        </button>
        <Input
          value={item.name || ""}
          onChange={(e) => onUpdate({ name: e.target.value })}
          placeholder={`Menu ${idx + 1}`}
          className="h-7 flex-1 text-xs"
          data-testid={testIdBase}
        />
        <span
          className={`shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase ${rating.color}`}
        >
          {rating.label}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          className="h-6 w-6 shrink-0 text-destructive hover:bg-destructive/10"
          data-testid={`${testIdBase}-remove`}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
      {expanded && (
        <div className="space-y-1.5 border-t bg-white px-2 py-1.5">
          {/* Rating pills */}
          <div className="flex items-center gap-1.5">
            <Label className="shrink-0 text-[9px] font-semibold uppercase tracking-wider text-slate-600">
              Rating
            </Label>
            <div className="flex gap-1">
              {ratings.map((r) => (
                <button
                  key={String(r.v)}
                  type="button"
                  onClick={() => onUpdate({ rating: r.v })}
                  className={`rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase ${
                    (item.rating || null) === r.v
                      ? r.color + " ring-1 ring-amber-400"
                      : "bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`${testIdBase}-rating-${r.v || "null"}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          {/* Comment */}
          <Input
            value={item.comment || ""}
            onChange={(e) => onUpdate({ comment: e.target.value })}
            placeholder='e.g. "Formal Night Menu", "Seafood Sunday"'
            className="h-7 text-[11px] italic"
            data-testid={`${testIdBase}-comment`}
          />
          {/* Courses */}
          <div className="space-y-1">
            <Label className="text-[9px] font-semibold uppercase tracking-wider text-slate-600">
              Courses
            </Label>
            {(item.courses || []).map((course, ci) => (
              <CourseRow
                key={course.id || ci}
                course={course}
                onUpdate={(patch) => {
                  const next = [...(item.courses || [])];
                  next[ci] = { ...next[ci], ...patch };
                  onUpdate({ courses: next });
                }}
                onRemove={() => {
                  const next = [...(item.courses || [])];
                  next.splice(ci, 1);
                  onUpdate({ courses: next });
                }}
                testIdBase={`${testIdBase}-course-${ci}`}
              />
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                onUpdate({
                  courses: [
                    ...(item.courses || []),
                    {
                      id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                      name: "",
                      dishes: [],
                    },
                  ],
                })
              }
              className="h-6 text-[10px]"
              data-testid={`${testIdBase}-add-course`}
            >
              <Plus className="h-3 w-3" />
              Add course
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CourseRow({ course, onUpdate, onRemove, testIdBase }) {
  const [dishExpanded, setDishExpanded] = useState(false);
  return (
    <div className="rounded border bg-slate-50/60 p-1.5">
      <div className="flex items-center gap-1.5">
        <Input
          value={course.name || ""}
          onChange={(e) => onUpdate({ name: e.target.value })}
          placeholder="Course name (e.g. Appetizers)"
          className="h-6 flex-1 text-[11px]"
          data-testid={`${testIdBase}-name`}
        />
        <button
          type="button"
          onClick={() => setDishExpanded((e) => !e)}
          className="rounded bg-white px-1.5 py-0.5 text-[9px] text-muted-foreground hover:bg-slate-100"
          data-testid={`${testIdBase}-dish-toggle`}
        >
          {course.dishes?.length || 0} dishes {dishExpanded ? "▾" : "▸"}
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          className="h-5 w-5 text-destructive"
          data-testid={`${testIdBase}-remove`}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
      {dishExpanded && (
        <div className="mt-1 space-y-1 border-t pt-1">
          {(course.dishes || []).map((d, di) => (
            <div key={di} className="flex items-center gap-1.5">
              <Input
                value={d || ""}
                onChange={(e) => {
                  const next = [...(course.dishes || [])];
                  next[di] = e.target.value;
                  onUpdate({ dishes: next });
                }}
                placeholder={`Dish ${di + 1}`}
                className="h-6 flex-1 text-[10px]"
                data-testid={`${testIdBase}-dish-${di}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => {
                  const next = [...(course.dishes || [])];
                  next.splice(di, 1);
                  onUpdate({ dishes: next });
                }}
                className="h-5 w-5 text-destructive"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              onUpdate({ dishes: [...(course.dishes || []), ""] })
            }
            className="h-5 text-[9px]"
            data-testid={`${testIdBase}-dish-add`}
          >
            <Plus className="h-3 w-3" />
            Add dish
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Phase 7 — Service Times for regular restaurants.
 *
 * Collapsible block with one row per service slot
 * (Embarkation Breakfast / Breakfast / Lunch / Embarkation Lunch / Dinner).
 * Each row has start + end HH:MM inputs + a Clear button. Empty / partial
 * slots are dropped before save; only fully-filled HH:MM pairs are sent
 * to the backend.
 *
 * Defaults aren't auto-populated — the form starts empty (per spec). The
 * "Use default" button on each row is a convenience for the operator;
 * Clear wipes the slot back to unset.
 */
function RegularServiceTimes({
  defaultTimes, expanded, onToggleExpanded,
  onSet, onClear, onFillDefault,
}) {
  const filledCount = Object.keys(defaultTimes || {}).length;
  return (
    <div
      className="rounded-md border bg-slate-50/50"
      data-testid="form-service-times-section"
    >
      <button
        type="button"
        onClick={onToggleExpanded}
        className="flex w-full items-center justify-between px-3 py-2.5 text-left"
        data-testid="form-service-times-toggle"
      >
        <div>
          <Label className="cursor-pointer text-sm font-semibold">
            Service Times <span className="text-muted-foreground">(optional)</span>
          </Label>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            Default open/close times per service slot. Leave any row empty
            to skip. Voyage editor pre-fills these into each day's row.
          </p>
        </div>
        <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
          {filledCount > 0 ? `${filledCount} set` : "0 set"}
        </Badge>
      </button>
      {expanded && (
        <div
          className="space-y-2 border-t bg-white px-3 py-3"
          data-testid="form-service-times-body"
        >
          {REGULAR_SLOTS.map(({ key, label, hint }) => {
            const t = (defaultTimes || {})[key] || {};
            const start = t.start || "";
            const end = t.end || "";
            const startBad = start && !isHHMM(start);
            const endBad = end && !isHHMM(end);
            const order = (
              isHHMM(start) && isHHMM(end) && !isEndAfterStart(start, end)
            );
            const isEmbark = key.startsWith("embarkation_");
            return (
              <div
                key={key}
                className={
                  "rounded-md border p-2 " +
                  (isEmbark ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-white")
                }
                data-testid={`form-time-row-${key}`}
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold">{label}</span>
                      {isEmbark && (
                        <span className="rounded-full bg-amber-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-900">
                          Day 1
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground">{hint}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="time"
                      value={start}
                      onChange={(e) => onSet(key, "start", e.target.value)}
                      className={
                        "h-7 w-24 text-xs " + (startBad ? "border-destructive" : "")
                      }
                      data-testid={`form-time-${key}-start`}
                      placeholder="HH:MM"
                    />
                    <span className="text-muted-foreground">–</span>
                    <Input
                      type="time"
                      value={end}
                      onChange={(e) => onSet(key, "end", e.target.value)}
                      className={
                        "h-7 w-24 text-xs " + (endBad ? "border-destructive" : "")
                      }
                      data-testid={`form-time-${key}-end`}
                      placeholder="HH:MM"
                    />
                    {!start && !end && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onFillDefault(key)}
                        className="h-7 px-2 text-[10px]"
                        data-testid={`form-time-${key}-default`}
                      >
                        Use default
                      </Button>
                    )}
                    {(start || end) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onClear(key)}
                        className="h-7 px-2 text-[10px] text-destructive hover:bg-destructive/10"
                        data-testid={`form-time-${key}-clear`}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
                {order && (
                  <p
                    className="mt-1 text-[10px] text-amber-700"
                    data-testid={`form-time-${key}-warn-order`}
                  >
                    ⚠ End is not after Start — double-check the times.
                  </p>
                )}
                {(startBad || endBad) && (
                  <p
                    className="mt-1 text-[10px] text-destructive"
                    data-testid={`form-time-${key}-warn-format`}
                  >
                    HH:MM required.
                  </p>
                )}
              </div>
            );
          })}
          <p className="text-[10px] italic text-muted-foreground">
            Tip: only fill the slots that apply. Empty rows are dropped on
            save — no need to delete them.
          </p>
        </div>
      )}
    </div>
  );
}
